import { CreditCard, MapPin, Plus, ShoppingBag } from "lucide-react";
import { textoVariacoes } from "shared/utils/carrinho";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Input } from "shared/components/inputs/Input";
import PageContainer from "shared/components/pagecontainer/PageContainer";
import { CartContext } from "shared/contexts/Cart/CartContext";
import { IEnderecoUser } from "shared/interfaces/IEnderecoUser";
import { buscarTodosCupons } from "shared/services/api/cupons.api";
import { buscarEnderecos } from "shared/services/api/endereco.api";
import { formatarMoeda } from "shared/utils/money";
import { calcularFreteCarrinho } from "shared/services/api/delivery.api";
import styles from "./FinalizarPedido.module.scss";
import { ICartaoView, IInstallment } from "shared/interfaces/IMetodo";
import { buscarCartoes } from "shared/services/api/cartao.api";
import { ModalCartoes } from "shared/components/modal/ModalCartoes";
import { IPedidoFinalizar } from "shared/interfaces";
import { finalizarPedido } from "shared/services/api/pedido.api";
import { featureFlags } from "shared/config/featureFlags";
import { mockCheckoutResponse } from "shared/utils/mockCheckoutResponse";
import { CadastroRealizado } from "shared/components/modal/CadastroRealizado";

export const FinalizarPedido = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, clearCart } = useContext(CartContext);

  const [frete, setFrete] = useState(0);
  const [freteStatus, setFreteStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const enderecoRetornado = location.state?.enderecoSelecionado;
  const [enderecos, setEnderecos] = useState<IEnderecoUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [enderecoSelecionado, setEnderecoSelecionado] = useState<IEnderecoUser | null>(null);
  const [formaPagamento, setFormaPagamento] = useState<"cartao" | "pix" | "boleto">("boleto");
  const [cupom, setCupom] = useState("");
  const [cupons, setCupons] = useState<any[]>([]);
  const [openCartoes, setOpenCartoes] = useState(false);
  const [cartoes, setCartoes] = useState<ICartaoView[]>([]);
  const [cartaoSelecionado, setCartaoSelecionado] = useState<ICartaoView | null>(null);
  const [parcelaSelecionada, setParcelaSelecionada] = useState<IInstallment | null>(null);
  const [cardCvv, setCardCvv] = useState("");
  const [compraRealizada, setCompraRealizada] = useState(false);
  const [respostaPedido, setRespostaPedido] = useState<any>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [finalizando, setFinalizando] = useState(false);

  const tentativaIniciada = useRef(false);
  const enderecoAnterior = useRef<string | null>(null);

  const subtotal = useMemo(() => cart.reduce((acc, item) => acc + item.preco * item.unidades, 0), [cart]);
  const totalFinal = subtotal + frete;

  const iniciarNovaTentativa = () => {
    if (tentativaIniciada.current) setIdempotencyKey(crypto.randomUUID());
  };

  useEffect(() => {
    async function carregar() {
      try {
        const lista = await buscarEnderecos();
        setEnderecos(lista);

        if (enderecoRetornado) {
          setEnderecoSelecionado(enderecoRetornado);
        } else {
          const principal = lista.find((e) => e.enderecoPrincipal);
          setEnderecoSelecionado(principal || lista[0] || null);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }

    carregar();
  }, [enderecoRetornado]);

  useEffect(() => {
    const novoEnderecoId = enderecoSelecionado?.id ?? null;

    if (enderecoAnterior.current === null) {
      enderecoAnterior.current = novoEnderecoId;
      return;
    }

    if (enderecoAnterior.current !== novoEnderecoId) {
      iniciarNovaTentativa();
      enderecoAnterior.current = novoEnderecoId;
    }
  }, [enderecoSelecionado?.id]);

  useEffect(() => {
    async function carregarFrete() {
      if (!enderecoSelecionado) {
        setFreteStatus("idle");
        return;
      }

      setFreteStatus("loading");

      try {
        const response = await calcularFreteCarrinho({ addressId: enderecoSelecionado.id });
        setFrete(response.totalCents);
        setFreteStatus("success");
      } catch (error) {
        console.error(error);
        setFrete(0);
        setFreteStatus("error");
      }
    }

    carregarFrete();
  }, [enderecoSelecionado]);

  useEffect(() => {
    async function carregarCupons() {
      try {
        const lista = await buscarTodosCupons();
        setCupons(lista);
      } catch (error) {
        console.error(error);
      }
    }

    carregarCupons();
  }, []);

  useEffect(() => {
    async function carregarCartoes() {
      try {
        const lista = await buscarCartoes();
        setCartoes(lista);
      } catch (error) {
        console.error(error);
      }
    }

    carregarCartoes();
  }, []);

  const handleContinuarCompra = () => {
    if (!respostaPedido) return;

    const checkout = { endereco: enderecoSelecionado, frete, formaPagamento, cupom };
    setCompraRealizada(false);

    if (formaPagamento === "pix") {
      navigate("/pagamento/pix", {
        state: {
          ...checkout,
          valor: formatarMoeda(totalFinal),
          qrCode: respostaPedido.paymentDetails?.qrCode,
          qrCodeUrl: respostaPedido.paymentDetails?.qrCodeUrl,
        },
      });
      return;
    }

    if (formaPagamento === "boleto") {
      navigate("/pagamento/boleto", {
        state: {
          ...checkout,
          valor: formatarMoeda(totalFinal),
          boletoUrl: respostaPedido.paymentDetails?.pdf || respostaPedido.paymentDetails?.url,
          barcode: respostaPedido.paymentDetails?.barcode,
        },
      });
      return;
    }

    navigate("/pedido/sucesso", { state: respostaPedido });
  };

  const handleContinuar = async () => {
    if (finalizando) return;

    if (!enderecoSelecionado) {
      alert("Selecione um endereço.");
      return;
    }

    if (cart.length === 0) {
      alert("Seu carrinho está vazio.");
      return;
    }

    if (freteStatus !== "success") {
      alert("Aguarde o cálculo do frete para continuar.");
      return;
    }

    if (formaPagamento === "cartao") {
      if (!cartaoSelecionado) {
        alert("Selecione um cartão.");
        return;
      }

      if (!parcelaSelecionada) {
        alert("Selecione uma parcela.");
        return;
      }

      if (!cardCvv.trim()) {
        alert("Informe o CVV do cartão.");
        return;
      }

      if (cardCvv.length < 3) {
        alert("O CVV é inválido.");
        return;
      }
    }

    try {
      setFinalizando(true);
      tentativaIniciada.current = true;

      const pedido: IPedidoFinalizar = {
        shippingAddressId: enderecoSelecionado.id,
        billingAddressId: enderecoSelecionado.id,
        paymentMethod: formaPagamento === "cartao" ? "CREDIT_CARD" : formaPagamento === "pix" ? "PIX" : "BOLETO",
        creditCardId: formaPagamento === "cartao" ? cartaoSelecionado?.id : undefined,
        cardCvv: formaPagamento === "cartao" ? cardCvv : undefined,
        installments: formaPagamento === "cartao" ? parcelaSelecionada?.installments : undefined,
        totalAmount: formaPagamento === "cartao" && parcelaSelecionada ? parcelaSelecionada.totalAmount : totalFinal,
        couponCode: cupom || undefined,
      };

      const resposta = featureFlags.mockCheckoutResponse
        ? mockCheckoutResponse(formaPagamento as "pix" | "boleto" | "cartao")
        : await finalizarPedido(pedido, idempotencyKey);

      await clearCart();

      const checkout = { endereco: enderecoSelecionado, frete, formaPagamento, cupom };
      localStorage.setItem("checkout", JSON.stringify(checkout));

      setRespostaPedido(resposta);
      setCompraRealizada(true);
    } catch (error: any) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (status === 409 && (code === "IDEMPOTENCY_IN_FLIGHT" || code === "CONCURRENT_REQUEST_IN_PROGRESS")) {
        alert("O pedido já está sendo processado. Aguarde alguns instantes antes de tentar novamente.");
        return;
      }

      if (status === 422 && code === "IDEMPOTENCY_KEY_REUSED") {
        alert("A chave de idempotência já foi utilizada para outro pedido. Não tente novamente com essa tentativa.");
        return;
      }

      if (status === 500 && code === "PAYMENT_SETTLEMENT_UNCONFIRMED") {
        alert("Não foi possível confirmar o pedido. O pagamento pode ter sido realizado. Não tente novamente.");
        return;
      }

      if (status === 503 && code === "IDEMPOTENCY_UNAVAILABLE") {
        alert("O serviço está temporariamente indisponível. Você pode tentar novamente.");
        return;
      }

      alert("Não foi possível finalizar o pedido.");
    } finally {
      setFinalizando(false);
    }
  };

  return (
    <PageContainer title="Finalizar Pedido">
      <div className={styles.page}>
        <div className={styles.containerResumo}>
          <div className={styles.containerTResumo}>
            <div className={styles.containerIconTitulo}><ShoppingBag size={18} color="var(--secondary-2)" /><h4>Resumo do Pedido</h4></div>
          </div>

          {cart.map((item) => (
            <div key={item.id} className={styles.produtoResumo}>
              <img alt="imagem" src={item.gallery || "/imagens/default.png"} className={styles.imgProduto} />
              <div className={styles.infoProduto}><p className={styles.nomeProduto}>{item.name}</p>{textoVariacoes(item) && <p className={styles.variacoes}>{textoVariacoes(item)}</p>}<p className={styles.qtd}>Qtd: {item.unidades}</p><p className={styles.precoProduto}>{formatarMoeda(item.preco)}</p></div>
            </div>
          ))}

          <hr className={styles.divisor} />
          <div className={styles.linha}><p>Subtotal:</p><p>{formatarMoeda(subtotal)}</p></div>
          <div className={styles.linha}><p>Frete:</p>{freteStatus === "loading" && <p>Calculando...</p>}{freteStatus === "error" && <p>Não foi possível calcular</p>}{freteStatus === "success" && <p className={frete === 0 ? styles.gratis : ""}>{frete === 0 ? "Grátis" : formatarMoeda(frete)}</p>}</div>
          <hr className={styles.divisor} />
          <div className={styles.total}><p>Total:</p><p>{formatarMoeda(totalFinal)}</p></div>
        </div>

        {!loading && enderecos.length === 0 && (
          <div className={styles.containerVazio}>
            <div className={styles.containerTVazio}><MapPin size={18} color="var(--secondary-2)" /><h4>Meus Endereços</h4></div>
            <p>Nenhum endereço encontrado</p>
            <div className={styles.addEndereco} onClick={() => navigate("/meusenderecos")}><Plus size={14} /><p>Adicionar endereço</p></div>
          </div>
        )}

        {!loading && enderecos.length > 0 && enderecoSelecionado && (
          <div className={styles.containerEnderecos}>
            <div className={styles.containerTVazio}><MapPin size={18} color="var(--secondary-2)" /><h4>Meus Endereços</h4></div>
            <div className={styles.containerEndereco}><p className={styles.tipoResidencia}>{enderecoSelecionado.nome}</p><p>{enderecoSelecionado.rua}, {enderecoSelecionado.numero} - {enderecoSelecionado.bairro}, {enderecoSelecionado.cidade} - {enderecoSelecionado.estado}</p><p>CEP: {enderecoSelecionado.cep}</p></div>
            <div className={styles.alterarEndereco} onClick={() => navigate("/meusenderecos", { state: { selecionarEndereco: true, returnTo: "/carrinho/finalizar" } })}><Plus size={14} /><p>Alterar endereço</p></div>
          </div>
        )}

        <div className={styles.containerPagamento}>
          <div className={styles.containerTPagamento}><CreditCard size={18} color="var(--secondary-2)" /><h4>Formas de Pagamento</h4></div>

          <div className={styles.opcoesPagamento}>
            <div
              className={`${styles.opcao} ${formaPagamento === "cartao" ? styles.selected : ""}`}
              onClick={() => {
                setFormaPagamento("cartao");
                iniciarNovaTentativa();
                setOpenCartoes(true);
              }}
            >
              {!cartaoSelecionado ? <span>Cartão de Crédito</span> : (
                <div className={styles.cartaoHeader}>
                  <div className={styles.cartaoEsquerda}>{cartaoSelecionado.bandeira && <img src={cartaoSelecionado.bandeira} alt={cartaoSelecionado.marca} className={styles.bandeira} />}</div>
                  <span className={styles.nomeCartao}>{cartaoSelecionado.marca}</span>
                  <div className={styles.cartaoDireita}>{parcelaSelecionada && <div className={styles.parcelaCartao}>{parcelaSelecionada.installments}x de {formatarMoeda(parcelaSelecionada.installmentAmount)}</div>}<span className={styles.finalCartao}>* {cartaoSelecionado.ultimos4digitos}</span></div>
                </div>
              )}
            </div>

            {formaPagamento === "cartao" && cartaoSelecionado && (
              <div className={styles.containerCvv}>
                <input name="cardCvv" type="password" placeholder="Digite o CVV" className={styles.inputCvv} value={cardCvv} maxLength={4} onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, ""))} />
              </div>
            )}

            <div className={`${styles.opcao} ${formaPagamento === "pix" ? styles.selected : ""}`} onClick={() => { setFormaPagamento("pix"); iniciarNovaTentativa(); }}>Pix</div>

            <div className={`${styles.opcao} ${formaPagamento === "boleto" ? styles.selected : ""}`} onClick={() => { setFormaPagamento("boleto"); iniciarNovaTentativa(); }}>Boleto Bancário</div>
          </div>
        </div>

        <div className={styles.containerCupom}>
          <div className={styles.containerTCupom}><MapPin size={18} color="var(--secondary-2)" /><h4>Cupom de Desconto</h4></div>

          <div className={styles.containerInputCupom}>
            <Input
              name="cupom"
              type="text"
              placeholder="Digite o cupom"
              value={cupom}
              onChange={(e) => setCupom(e.target.value)}
              customClass={styles.customInput}
            />
            <button className={styles.btnAplicar} onClick={iniciarNovaTentativa}>Aplicar</button>
          </div>

          <div className={styles.cuponsDisponiveis}>
            <p>Cupons disponíveis:</p>
            {cupons.length === 0 ? <p>Nenhum cupom disponível</p> : <ul>{cupons.map((c) => <li key={c.uid}>{c.codigo} - {c.desconto ?? c.valor}% de desconto</li>)}</ul>}
          </div>
        </div>

        {enderecoSelecionado && (
          <div className={styles.containerBtn}>
            <button className={styles.continuar} onClick={handleContinuar} disabled={freteStatus !== "success" || finalizando}>
              {freteStatus === "loading" ? "Calculando frete..." : finalizando ? "Finalizando..." : "Finalizar Pedido"}
            </button>
          </div>
        )}
      </div>

      <ModalCartoes
        open={openCartoes}
        cards={cartoes}
        selectedCard={cartaoSelecionado?.id}
        onClose={() => setOpenCartoes(false)}
        total={totalFinal}
        onSelect={(card, parcela) => {
          setCartaoSelecionado(card);
          setParcelaSelecionada(parcela);
          setFormaPagamento("cartao");
          iniciarNovaTentativa();
          setOpenCartoes(false);
        }}
        onAddCard={() => navigate("/metodopagamento", { state: { returnTo: "/carrinho/finalizar" } })}
      />

      <CadastroRealizado
        open={compraRealizada}
        mensagem="Sua compra foi realizada com sucesso!"
        descricao="Seu pedido foi registrado e já está sendo processado."
        detalheLabel="Pedido"
        detalheValor={respostaPedido?.id?`#${respostaPedido.id}`:undefined}
        textoBotao="Continuar"
        onClose={()=>setCompraRealizada(false)}
        onContinuar={handleContinuarCompra}
      />
    </PageContainer>
  );
};